import * as PIXI from 'pixi.js';
import { imageAssets, audioAssets, type ImageAssetConfig, type AudioAssetConfig } from '@/config/AssetConfig';
import { SoundManager } from '@/systems';

export interface AssetLoadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentAsset?: string;
  stage: 'images' | 'audio' | 'complete';
}

export type ProgressCallback = (progress: AssetLoadProgress) => void;

/**
 * AssetManager handles preloading of all game assets (images and audio)
 * Provides progress tracking and ensures assets are available before game starts
 */
export class AssetManager {
  private static instance: AssetManager;
  private imageTextures: Map<string, PIXI.Texture> = new Map();
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private loadedImages: Set<string> = new Set();
  private loadedAudio: Set<string> = new Set();
  private isLoading = false;
  private soundManager: SoundManager;

  private constructor() {
    this.soundManager = SoundManager.getInstance();
  }

  public static getInstance(): AssetManager {
    if (!AssetManager.instance) {
      AssetManager.instance = new AssetManager();
    }
    return AssetManager.instance;
  }

  /**
   * Preload all assets (images and audio) with progress tracking
   */
  public async preloadAllAssets(onProgress?: ProgressCallback): Promise<void> {
    if (this.isLoading) {
      throw new Error('Asset loading is already in progress');
    }

    this.isLoading = true;

    try {
      console.log('Starting preload of all assets...');
      
      const totalAssets = imageAssets.length + audioAssets.length;
      let loadedCount = 0;

      const updateProgress = (stage: 'images' | 'audio' | 'complete', currentAsset?: string) => {
        if (onProgress) {
          onProgress({
            loaded: loadedCount,
            total: totalAssets,
            percentage: Math.round((loadedCount / totalAssets) * 100),
            currentAsset,
            stage,
          });
        }
      };

      // Load images in parallel
      console.log(`Loading ${imageAssets.length} image assets in parallel...`);
      updateProgress('images');

      const imageLoadPromises = imageAssets
        .filter(asset => asset.preload !== false)
        .map(async (imageAsset) => {
          try {
            await this.loadImageAsset(imageAsset);
            loadedCount++;
            updateProgress('images', imageAsset.id);
            return { success: true, id: imageAsset.id };
          } catch (error) {
            console.warn(`⚠️ Skipping missing/corrupted image asset ${imageAsset.id}:`, error);
            loadedCount++; // Still count as processed
            updateProgress('images', imageAsset.id);
            return { success: false, id: imageAsset.id, error };
          }
        });

      await Promise.all(imageLoadPromises);

      // Skip AudioContext initialization during preload
      // AudioContext will be initialized on first user interaction

      // Load audio assets in parallel
      console.log(`Loading ${audioAssets.length} audio assets in parallel...`);
      updateProgress('audio');

      const audioLoadPromises = audioAssets
        .filter(asset => asset.preload !== false)
        .map(async (audioAsset) => {
          try {
            await this.loadAudioAsset(audioAsset);
            loadedCount++;
            updateProgress('audio', audioAsset.id);
            return { success: true, id: audioAsset.id };
          } catch (error) {
            console.warn(`Failed to load audio asset ${audioAsset.id}:`, error);
            loadedCount++; // Still count as processed
            updateProgress('audio', audioAsset.id);
            return { success: false, id: audioAsset.id, error };
          }
        });

      await Promise.all(audioLoadPromises);

      updateProgress('complete');
      console.log(`Asset preloading complete: ${loadedCount}/${totalAssets} assets processed`);
      
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load a single image asset
   */
  private async loadImageAsset(asset: ImageAssetConfig): Promise<void> {
    if (this.loadedImages.has(asset.id)) {
      return; // Already loaded
    }

    try {
      // Use PIXI.Assets.load for better caching and error handling
      const texture = await PIXI.Assets.load(asset.src);
      this.imageTextures.set(asset.id, texture);
      this.loadedImages.add(asset.id);
      console.log(`✅ Loaded image: ${asset.id}`);
    } catch (error) {
      console.error(`❌ Failed to load image ${asset.id} from ${asset.src}:`, error);
      throw error;
    }
  }

  /**
   * Load a single audio asset
   */
  private async loadAudioAsset(asset: AudioAssetConfig): Promise<void> {
    if (this.loadedAudio.has(asset.id)) {
      return; // Already loaded
    }

    try {
      // Register with sound manager first
      // Note: Type assertion needed due to SoundManager interface mismatch
      this.soundManager.addSound(asset.id, {
        src: asset.src,
        category: asset.category,
        loop: asset.loop,
        volume: asset.volume,
        preload: asset.preload,
      } as Parameters<typeof this.soundManager.addSound>[1]);

      // For now, just mark audio as loaded since it's registered with sound manager
      // The sound manager will handle the actual loading when audio context is available
      this.loadedAudio.add(asset.id);
      console.log(`✅ Registered audio: ${asset.id}`);
      
      // Note: Actual audio buffer loading will be handled by SoundManager
      // when the audio context is properly initialized after user interaction
    } catch (error) {
      console.error(`❌ Failed to load audio ${asset.id} from ${asset.src}:`, error);
      throw error;
    }
  }

  /**
   * Get a preloaded image texture
   */
  public getImageTexture(id: string): PIXI.Texture | undefined {
    return this.imageTextures.get(id);
  }

  /**
   * Get a preloaded audio buffer
   */
  public getAudioBuffer(id: string): AudioBuffer | undefined {
    return this.audioBuffers.get(id);
  }

  /**
   * Check if an image asset is loaded
   */
  public isImageLoaded(id: string): boolean {
    return this.loadedImages.has(id);
  }

  /**
   * Check if an audio asset is loaded
   */
  public isAudioLoaded(id: string): boolean {
    return this.loadedAudio.has(id);
  }

  /**
   * Check if all assets are loaded
   */
  public areAllAssetsLoaded(): boolean {
    const imageCount = imageAssets.filter(a => a.preload !== false).length;
    const audioCount = audioAssets.filter(a => a.preload !== false).length;
    
    return this.loadedImages.size >= imageCount && this.loadedAudio.size >= audioCount;
  }

  /**
   * Get loading statistics
   */
  public getLoadingStats(): { images: number, audio: number, total: number } {
    return {
      images: this.loadedImages.size,
      audio: this.loadedAudio.size,
      total: this.loadedImages.size + this.loadedAudio.size,
    };
  }

  /**
   * Create a PIXI texture from a preloaded image
   * This is a convenience method for getting textures with fallback
   */
  public createTexture(id: string): PIXI.Texture {
    const texture = this.getImageTexture(id);
    if (texture) {
      return texture;
    }

    // Fallback: try to load from asset path if not preloaded
    console.warn(`Texture ${id} not preloaded, falling back to on-demand loading`);
    const asset = imageAssets.find(a => a.id === id);
    if (asset) {
      return PIXI.Texture.from(asset.src);
    }

    // Ultimate fallback: return empty texture
    console.error(`No asset configuration found for ${id}, returning empty texture`);
    return PIXI.Texture.EMPTY;
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    this.imageTextures.clear();
    this.audioBuffers.clear();
    this.loadedImages.clear();
    this.loadedAudio.clear();
  }
}
